/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddIcon,
  AddImageIcon,
  DownArrowIcon,
  UpArrowIcon,
  CorrectIcon,
  CancelIcon,
  DeleteIcon,
  SaveIcon,
} from 'src/v3/core/icons'
import { lensPath, set, view } from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchClasses,
  fetchQuizzes,
  selectActiveClass,
  selectClassByID,
  selectClassesStatus,
  selectQuizByClassID,
  selectQuizzesStatus,
} from 'src/v3/create/create.slice'
import Loading from 'src/v3/components/loading'

import theme from 'src/theme'
import axios, { pluckData } from 'src/utilities/http-client'

type AnswerRequest = {
  id: string
  text: string
  isAnswer: boolean
}

type QuestionRequest = {
  id: string
  text: string
  choices: AnswerRequest[]
  explanation: string
  figure?: string
}

type QuizRequest = {
  id: string
  classID: string
  name: string
  description: string
  passingScore: number
  questions: QuestionRequest[]
}

type getPresignedUrlResponse = {
  filename: string
  url: string
}

let createNewAnswer = (isAnswer: boolean): AnswerRequest => ({
  id: uuidv4(),
  text: '',
  isAnswer,
})

let createNewQuestion = (): QuestionRequest => ({
  id: uuidv4(),
  text: '',
  choices: [createNewAnswer(true), createNewAnswer(false)],
  explanation: '',
})

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

let getPresignedURL = (imageType: string) =>
  axios
    .get<getPresignedUrlResponse>(
      `/api/v2/create/quiz-image?ImageType=${imageType}`,
    )
    .then(pluckData)

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let classesStatus = useAppSelector(selectClassesStatus)
  let activeClass = useAppSelector(selectActiveClass)
  let classData = useAppSelector(selectClassByID(activeClass))
  let quizzesStatus = useAppSelector(selectQuizzesStatus)
  let existingQuiz = useAppSelector(selectQuizByClassID(activeClass))

  let [quiz, setQuiz] = useState<QuizRequest>()
  let [saving, setSaving] = useState<boolean>(false)
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let existingImages =
    existingQuiz?.questions?.reduce(
      (all, q) => {
        if (q.figureImageName) {
          // eslint-disable-next-line no-param-reassign
          all[q.id] = q.figureImageName
        }
        return all
      },
      {} as Record<string, string>,
    ) || {}

  let questionsLens = lensPath(['questions'])
  let passingScoreLens = lensPath(['passingScore'])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (quizzesStatus === 'uninitialized' && activeClass !== 'unselected') {
      dispatch(fetchQuizzes(activeClass))
    }
  }, [quizzesStatus, activeClass])

  useEffect(() => {
    if (quizzesStatus === 'fulfilled' && !existingQuiz) {
      dispatch(fetchQuizzes(activeClass))
    }
  }, [activeClass])

  useEffect(() => {
    if (quizzesStatus === 'fulfilled' && existingQuiz) {
      setQuiz({
        id: existingQuiz.id,
        classID: activeClass,
        name: existingQuiz.name,
        description: existingQuiz.name,
        passingScore: existingQuiz.passingScore,
        questions: existingQuiz.questions?.map((q) => ({
          id: q.id,
          text: q.text,
          explanation: q.explanationText,
          figure: q.figureImageName,
          choices: q.choices.map((c) => ({
            id: c.id,
            text: c.text,
            isAnswer: q.answerChoiceID === c.id,
          })),
        })),
      })
    }
  }, [quizzesStatus])

  useEffect(() => {
    if (activeClass === 'unselected') {
      navigate('/create')
    }
  }, [activeClass])

  let handleCapture = (event: React.FormEvent<HTMLInputElement>, i: number) => {
    let file = event.currentTarget.files![0]
    let reader = new FileReader()
    // eslint-disable-next-line fp/no-unused-expression,fp/no-nil,fp/no-mutation
    reader.onloadend = () => {
      let fileLens = lensPath(['questions', i, 'figure'])
      setQuiz(set(fileLens, reader.result ?? '', quiz))
    }
    // eslint-disable-next-line fp/no-unused-expression
    reader.readAsDataURL(file)
  }

  let postToPresignedURL = (
    image: string | ArrayBuffer | null,
    question: QuestionRequest,
  ): Promise<QuestionRequest> => {
    if (image === null || question.figure === existingImages[question.id]) {
      return Promise.resolve(question)
    }

    let fileData = image?.toString()?.match(/data:[a-z]+\/[a-z]+/)?.[0]

    if (!fileData) {
      setErrorMessage('Image Upload Failed: Read File Failed')
      return Promise.resolve(question)
    }

    let fileType = fileData.split(':')[1]
    let fileExtension = fileType.split('/')[1]

    let binary = atob(image.toString().split(',')[1] as string)
    let array = new Array(binary.length)
      .fill('')
      .map((_, i) => binary.charCodeAt(i))
    let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

    if (fileExtension === 'jpeg') {
      fileExtension = 'jpg'
    }

    return getPresignedURL(fileExtension).then(
      ({ url, filename }: getPresignedUrlResponse) =>
        axios
          .put(url, blobData, {
            headers: {
              'Cache-Control': 'max-age=2592000',
              'content-type': fileType,
            },
          })
          .then(() => ({ ...question, figure: filename }))
          .catch(() => ({ ...question, figure: filename })),
    )
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Quiz
        </Typography>
        <Typography paragraph className="ml-3 d-inline">
          Create a quiz for {classData?.name}
        </Typography>
        {!existingQuiz ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Typography sx={{ mb: 1 }}>
              No quiz exists for this class.
            </Typography>
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              disabled={activeClass === 'unselected'}
              onClick={() => {
                setSaving(true)
                axios
                  .post('/api/v2/create/quiz', {
                    name: `${classData?.name} Quiz`,
                    description: `Quiz for ${classData?.name}`,
                    classID: activeClass,
                    passingScore: 50,
                    questions: [],
                  })
                  .then(() => dispatch(fetchQuizzes(activeClass)))
                  .then(() => setSaving(false))
                  .catch(() => {
                    dispatch(fetchQuizzes(activeClass))
                    setSaving(false)
                  })
              }}
            >
              Create Quiz
            </Button>
          </Paper>
        ) : (
          <>
            {quiz?.questions?.map((q, i) => {
              let text = lensPath(['questions', i, 'text'])
              let answersLens = lensPath(['questions', i, 'choices'])
              let answers = view(answersLens, quiz)
              let explanation = lensPath(['questions', i, 'explanation'])

              return (
                <Card
                  variant="outlined"
                  key={q.id || `q-${i}`}
                  sx={{ px: 2, py: 2, mt: 2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'right',
                      alignItems: 'center',
                      mb: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mr: '2px',
                      }}
                    >
                      <IconButton
                        onClick={() =>
                          setQuiz(
                            set(
                              questionsLens,
                              moveItem(view(questionsLens, quiz), i, i - 1),
                              quiz,
                            ),
                          )
                        }
                        disabled={i === 0}
                        sx={{ height: '20px', width: '20px' }}
                      >
                        <UpArrowIcon />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          setQuiz(
                            set(
                              questionsLens,
                              moveItem(view(questionsLens, quiz), i, i + 1),
                              quiz,
                            ),
                          )
                        }
                        disabled={i === view(questionsLens, quiz).length - 1}
                        sx={{ height: '20px', width: '20px' }}
                      >
                        <DownArrowIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>
                      Question {i + 1}
                    </Typography>
                    <Tooltip title="Delete Question">
                      <span>
                        <IconButton
                          sx={{ width: '40px', ml: 1 }}
                          onClick={() =>
                            setQuiz(
                              set(
                                questionsLens,
                                view(questionsLens, quiz).filter(
                                  (ques: any) => ques.id !== q.id,
                                ),
                                quiz,
                              ),
                            )
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Question Text"
                    type="text"
                    fullWidth
                    multiline
                    value={view(text, quiz)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setQuiz(set(text, event.target.value, quiz))
                    }}
                  />
                  {q.figure ? (
                    <>
                      <div className="flex justify-end">
                        <Tooltip title="Remove Image" placement="top">
                          <IconButton
                            sx={{
                              mt: 2,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.background.paper,
                              '&:hover': {
                                backgroundColor: theme.palette.error.dark,
                              },
                            }}
                            onClick={() =>
                              setQuiz(
                                set(
                                  lensPath(['questions', i, 'figure']),
                                  undefined,
                                  quiz,
                                ),
                              )
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <CardMedia
                        sx={{
                          mt: -2.5,
                          px: 2.5,
                          maxHeight: '280px',
                          objectFit: 'contain',
                        }}
                        component="img"
                        src={
                          existingImages?.[q.id] && !q.figure.includes('base64')
                            ? `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${q.figure}`
                            : q.figure
                        }
                      />
                      <Typography
                        variant="body2"
                        textAlign="center"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        *Image may be significantly larger in the quiz
                      </Typography>
                    </>
                  ) : (
                    <label style={{ height: 0, marginBottom: 42 }}>
                      <Button
                        sx={{ mt: 1.5 }}
                        component="span"
                        variant="outlined"
                        size="small"
                        startIcon={<AddImageIcon />}
                      >
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          type="file"
                          onChange={(e) => handleCapture(e, i)}
                        />
                        Add Image
                      </Button>
                    </label>
                  )}
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Answers
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Use the buttons to the left to rearrange answers and mark
                    the correct one.
                  </Typography>
                  {answers.map((a: any, j: number) => {
                    let aText = lensPath(['questions', i, 'choices', j, 'text'])

                    return (
                      <Box
                        key={a.id || `a-${i}-${j}`}
                        sx={{
                          mt: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {!a.isAnswer ? (
                          <Tooltip title="Mark Answer Correct">
                            <IconButton
                              onClick={() =>
                                setQuiz(
                                  set(
                                    answersLens,
                                    answers.map((ans: any) => ({
                                      ...ans,
                                      isAnswer: ans.id === a.id,
                                    })),
                                    quiz,
                                  ),
                                )
                              }
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <CorrectIcon color="success" sx={{ mx: 1 }} />
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mr: '2px',
                          }}
                        >
                          <IconButton
                            onClick={() =>
                              setQuiz(
                                set(
                                  answersLens,
                                  moveItem(answers, j, j - 1),
                                  quiz,
                                ),
                              )
                            }
                            disabled={j === 0}
                            sx={{ height: '20px', width: '20px' }}
                          >
                            <UpArrowIcon />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              setQuiz(
                                set(
                                  answersLens,
                                  moveItem(answers, j, j + 1),
                                  quiz,
                                ),
                              )
                            }
                            disabled={j === answers.length - 1}
                            sx={{ height: '20px', width: '20px' }}
                          >
                            <DownArrowIcon />
                          </IconButton>
                        </Box>
                        <TextField
                          sx={{ flexGrow: 1 }}
                          variant="outlined"
                          size="small"
                          label={`Answer ${j + 1}`}
                          type="text"
                          fullWidth
                          multiline
                          value={view(aText, quiz)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setQuiz(set(aText, event.target.value, quiz))
                          }}
                        />
                        <Tooltip title="Delete Answer">
                          <span>
                            <IconButton
                              sx={{ width: '40px', ml: 1 }}
                              disabled={
                                !!answers[j].isAnswer || answers.length < 3
                              }
                              onClick={() =>
                                setQuiz(
                                  set(
                                    answersLens,
                                    view(answersLens, quiz).filter(
                                      (ans: any) => ans.id !== a.id,
                                    ),
                                    quiz,
                                  ),
                                )
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )
                  })}
                  <Button
                    sx={{ mt: 1.5 }}
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={answers.length >= 8}
                    onClick={() =>
                      setQuiz(
                        set(
                          answersLens,
                          [...answers, createNewAnswer(false)],
                          quiz,
                        ),
                      )
                    }
                  >
                    Add Answer
                  </Button>
                  <TextField
                    sx={{ mt: 2 }}
                    variant="standard"
                    size="small"
                    label="Answer Explanation"
                    type="text"
                    fullWidth
                    value={view(explanation, quiz)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setQuiz(set(explanation, event.target.value, quiz))
                    }}
                  />
                </Card>
              )
            })}
            <TextField
              sx={{ mt: 2 }}
              variant="outlined"
              size="small"
              label="Passing Grade"
              type="text"
              fullWidth
              value={quiz?.passingScore || 0}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setQuiz(
                  set(
                    passingScoreLens,
                    parseInt(event.target.value, 10) || 0,
                    quiz,
                  ),
                )
              }}
              onBlur={() => {
                let value = quiz?.passingScore || 0
                if (value > 100) value = 100
                if (value < 0) value = 0
                setQuiz(set(passingScoreLens, value, quiz))
              }}
            />
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() =>
                  setQuiz(
                    set(
                      questionsLens,
                      [
                        ...(view(questionsLens, quiz) || []),
                        createNewQuestion(),
                      ],
                      quiz,
                    ),
                  )
                }
              >
                Add New Question
              </Button>
              <Button
                variant="contained"
                size="medium"
                startIcon={<SaveIcon />}
                disabled={activeClass === 'unselected' || !quiz}
                onClick={() => {
                  if (
                    quiz?.questions.some(
                      (q) =>
                        !q.text ||
                        !q.explanation ||
                        q.choices.some((a) => !a.text),
                    )
                  ) {
                    setErrorMessage(
                      'All question field values must be filled in.',
                    )
                  } else if (
                    quiz?.questions.some((q) => {
                      let answers = q.choices.map((a) =>
                        a.text.toLowerCase().trim(),
                      )
                      return answers.length !== [...new Set(answers)].length
                    })
                  ) {
                    setErrorMessage('All question answers must be unique.')
                  } else {
                    setSaving(true)
                    Promise.all(
                      quiz?.questions.map((q) =>
                        postToPresignedURL(q.figure || null, q),
                      ) || [],
                    )
                      .then((questions) =>
                        axios.post('/api/v2/create/quiz', {
                          ...(quiz || {}),
                          questions: questions.map((q) => ({
                            text: q.text,
                            explanation: q.explanation,
                            figure: q.figure,
                            choices: q.choices.map((c) => ({
                              text: c.text,
                              isAnswer: c.isAnswer,
                            })),
                          })),
                        }),
                      )
                      .then(() => dispatch(fetchQuizzes(activeClass)))
                      .then(() => setSaving(false))
                      .then(() => setSuccessMessage('Quiz Saved'))
                      .catch(() => {
                        dispatch(fetchQuizzes(activeClass))
                        setSaving(false)
                      })
                  }
                }}
              >
                Save Quiz
              </Button>
            </Box>
          </>
        )}
      </Box>
      <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setSuccessMessage('')}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[classesStatus, quizzesStatus]} loading={saving} />
    </>
  )
}
